import React, { useEffect, useRef, useState } from 'react';
import { searchPlaces } from '../services';
import { hasGooglePlacesKey } from '../google/config';
import { useGoogleMaps } from '../google/GoogleMapsProvider';
import GooglePlacesAutocompleteInput from '../google/places/GooglePlacesAutocompleteInput';

/**
 * Unified address/place search input.
 * Uses Google Places Autocomplete when key is configured and loaded; else Nominatim.
 * Both normalize to { label, display_name, lat, lon, ... } for onSelect.
 */
export default function AddressSuggestInput({
  value,
  onChange,
  onSelect,
  placeholder,
  lang = 'original',
  bounds,
}) {
  const { isLoaded: isGoogleLoaded } = useGoogleMaps();
  const useGoogle = hasGooglePlacesKey() && isGoogleLoaded;

  if (useGoogle) {
    return (
      <GooglePlacesAutocompleteInput
        value={value}
        onChange={onChange}
        onSelect={onSelect}
        placeholder={placeholder}
        bounds={bounds}
      />
    );
  }

  return (
    <NominatimAddressSuggestInput
      value={value}
      onChange={onChange}
      onSelect={onSelect}
      placeholder={placeholder}
      lang={lang}
    />
  );
}

function NominatimAddressSuggestInput({ value, onChange, onSelect, placeholder, lang = 'original' }) {
  const [suggestions, setSuggestions] = useState([]);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [isFocused, setIsFocused] = useState(false);

  const hasUserTypedRef = useRef(false);
  const suppressNextFetchRef = useRef(false);

  useEffect(() => {
    if (!isFocused) {
      return;
    }

    if (suppressNextFetchRef.current) {
      suppressNextFetchRef.current = false;
      return;
    }

    if (!hasUserTypedRef.current) {
      return;
    }

    const q = String(value || '').trim();
    if (q.length < 2) {
      setSuggestions([]);
      setActiveIdx(-1);
      return;
    }

    const controller = new AbortController();
    const t = setTimeout(() => {
      searchPlaces(q, { limit: 6, lang, signal: controller.signal })
        .then(data => {
          if (Array.isArray(data)) {
            setSuggestions(data);
            setActiveIdx(-1);
          }
        })
        .catch(() => { /* ignore */ });
    }, 300);

    return () => {
      clearTimeout(t);
      controller.abort();
    };
  }, [value, lang, isFocused]);

  return (
    <div>
      <input
        type="text"
        placeholder={placeholder}
        value={value || ''}
        onChange={e => {
          hasUserTypedRef.current = true;
          onChange(e.target.value);
        }}
        onFocus={() => {
          setIsFocused(true);
        }}
        onBlur={() => {
          setIsFocused(false);
          hasUserTypedRef.current = false;
          // Delay clearing so a click on a suggestion can complete before dropdown vanishes
          setTimeout(() => {
            setSuggestions([]);
            setActiveIdx(-1);
          }, 150);
        }}
        onKeyDown={e => {
          if (!suggestions.length) return;
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIdx(i => (i + 1) % suggestions.length);
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIdx(i => (i <= 0 ? suggestions.length - 1 : i - 1));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            const idx = activeIdx >= 0 ? activeIdx : 0;
            const s = suggestions[idx];
            if (s) {
              suppressNextFetchRef.current = true;
              hasUserTypedRef.current = false;
              onSelect(s);
              setSuggestions([]);
              setActiveIdx(-1);
            }
          } else if (e.key === 'Escape') {
            setSuggestions([]);
            setActiveIdx(-1);
          }
        }}
      />

      {isFocused && suggestions.length > 0 && (
        <div
          className="tp-card tp-card--tight"
          style={{ marginTop: 10 }}
          onMouseDown={(e) => {
            // Prevent input from blurring when clicking dropdown so the click registers
            e.preventDefault();
          }}
        >
          {suggestions.map((s, idx) => (
            <div
              key={`${s.osm_type || 'osm'}-${s.osm_id || idx}`}
              onMouseDown={() => {
                suppressNextFetchRef.current = true;
                hasUserTypedRef.current = false;
                onSelect(s);
                setSuggestions([]);
                setActiveIdx(-1);
              }}
              style={{
                padding: '10px 12px',
                cursor: 'pointer',
                borderTop: idx === 0 ? 'none' : '1px solid var(--tp-border)',
                background: idx === activeIdx ? 'var(--tp-surface-2)' : 'transparent'
              }}
            >
              <div style={{ fontSize: 14 }}>{s.label || s.display_name}</div>
              {!!s.subtitle && (
                <div style={{ fontSize: 12, color: 'var(--tp-muted)', marginTop: 2 }}>{s.subtitle}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
